import { CoachActivityTypeEnum } from "@/modules/coach/coachActivityTypes"
import type { CoachSettingsEntity } from "@/modules/coach/entities/coachSettingsEntity"
import { type ProfessionalProfileInputDto } from "@/modules/coach/gateway/coachDto"
import { type MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { word } from "@/modules/core/utils/i18n"
import type { Language } from "@/modules/language/utils/languageUtils"
import type { UserProfileEntity } from "@/modules/users/entities/usersEntity"
import { z } from "zod"

export const getCoachFormDataDefaultValues = (coach: CoachSettingsEntity | null, user: UserProfileEntity) => {
  return {
    commercialName: coach?.state.commercialName ?? `${user.firstname} ${user.lastname}`,
    role: coach?.state.role ?? "",
    yearsOfExperience: coach?.state.yearsOfExperience?.toString() ?? "",
    city: coach?.state.city ?? "",
    region: coach?.state.region ?? "",
    country: coach?.state.country ?? "",
    phoneNumber: {
      number: coach?.state.phoneNumber ?? "",
      countryCode: coach?.state.phoneCountryCode ?? "",
    },
    email: coach?.state.email ?? user.email,
    description: coach?.state.description ?? "",
    formation: coach?.state.training ?? "",
    other1: coach?.state.additionalInformation[0] ?? "",
    other2: coach?.state.additionalInformation[1] ?? "",
    other3: coach?.state.additionalInformation[2] ?? "",
    profilePhoto: coach?.state.profilePhoto
      ? ({
          url: coach.state.profilePhoto.url,
          key: coach.state.profilePhoto.key ?? null,
          file: null,
          type: MediaType.S3_IMAGE,
          isUploading: false,
          filename: null,
        } satisfies MediaItemFormType as MediaItemFormType)
      : null,
    galleryImages: coach?.state.galleryImages
      ? (coach.state.galleryImages.map((img) => ({
          url: img.url,
          key: img.key ?? null,
          file: null,
          type: MediaType.S3_IMAGE,
          isUploading: false,
          filename: null,
        })) satisfies MediaItemFormType[] as MediaItemFormType[])
      : [],
  }
}

export const coachCreationFormSchema = (_language: Language) =>
  z.object({
    firstname: z.string().min(1, word("global.form.required")),
    lastname: z.string().min(1, word("global.form.required")),
    commercialName: z.string(),
    email: z.email(word("box.form.email.error")),
    phoneNumber: z.string(),
    country: z.string().min(1, word("global.form.required")),
    city: z.string(),
    description: z.string(),
    image: z.custom<MediaItemFormType | null>().refine((value) => !!value?.key),
    activityType: z.enum(CoachActivityTypeEnum, { error: word("global.form.required") }),
  })

export type CoachFormDataType = ReturnType<typeof getCoachFormDataDefaultValues>

export const coachFormDataDefaultValues: CoachFormDataType = {
  commercialName: "",
  role: "",
  yearsOfExperience: "",
  city: "",
  region: "",
  country: "",
  phoneNumber: { number: "", countryCode: "" },
  email: "",
  description: "",
  formation: "",
  other1: "",
  other2: "",
  other3: "",
  profilePhoto: null,
  galleryImages: [],
}

export const coachFormDataSchema = (_lang: Language) =>
  z.object({
    commercialName: z.string().min(1, word("global.form.required")),
    email: z.email(word("box.form.email.error")),
    role: z.string().min(1, word("global.form.required")),
    yearsOfExperience: z.string().min(1, word("global.form.required")),
    country: z.string().min(1, word("global.form.required")),
    description: z.string().min(1, word("global.form.required")),
    phoneNumber: z.object({ number: z.string(), countryCode: z.string() }),
    profilePhoto: z.custom<MediaItemFormType | null>(),
    galleryImages: z.array(z.custom<MediaItemFormType>()),
    city: z.string(),
    region: z.string(),
    formation: z.string(),
    other1: z.string(),
    other2: z.string(),
    other3: z.string(),
  })

export const convertCoachFormDataToInputDto = (
  value: CoachFormDataType,
  selectedActivities: CoachActivityTypeEnum[],
  otherActivities: string[],
): ProfessionalProfileInputDto => {
  const activityTypes = [
    ...selectedActivities.map((type, index) => ({
      id: null,
      orderIndex: index,
      type,
      otherType: null,
      principal: true,
      creationDate: null,
      archived: false,
      archivedDate: null,
    })),
    ...otherActivities.filter(Boolean).map((v, i) => ({
      id: null,
      orderIndex: selectedActivities.length + i,
      type: CoachActivityTypeEnum.OTHER,
      otherType: v,
      principal: false,
      creationDate: null,
      archived: false,
      archivedDate: null,
    })),
  ]

  const additionalInfo = [value.other1, value.other2, value.other3].filter(Boolean).map((information, idx) => ({
    id: null,
    orderIndex: idx,
    information,
    creationDate: null,
    archived: false,
    archivedDate: null,
  }))

  return {
    role: value.role || null,
    firstname: null,
    lastname: null,
    commercialName: value.commercialName || null,
    yearsOfExperience: value.yearsOfExperience ? parseInt(value.yearsOfExperience, 10) : null,
    email: value.email,
    phoneNumber:
      value.phoneNumber.number && value.phoneNumber.countryCode
        ? { number: value.phoneNumber.number, countryCode: value.phoneNumber.countryCode }
        : null,
    description: value.description || null,
    city: value.city || null,
    region: value.region || null,
    country: value.country,
    imageKey: value.profilePhoto?.key ?? "",
    activityTypes,
    training: value.formation || null,
    imageKeys: null,
    additionalInformation: additionalInfo,
  }
}
